import { Badge, Box, Button, Flex, HStack, Icon, Image, Text, VStack } from '@chakra-ui/react';
import { TicketResponse } from 'api/responseTypes';
import { Navbar } from 'components/Navbar/Navbar';
import html2canvas from 'html2canvas';
import { CalendarIcon, ClockIcon, MapPinIcon, UserIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'utils/toast';
import { QrCode } from '../QrCode/QrCode';
import { QrCodeSuccess } from '../QrCode/QrCodeSuccess/QrCodeSuccess';

interface InvitationTicketGeneratorProps {
  data: TicketResponse;
}

const QrPlaceholderIcon = () => (
  <svg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect x='4' y='4' width='14' height='14' rx='2' stroke='#E57373' strokeWidth='2' />
    <rect x='4' y='22' width='14' height='14' rx='2' stroke='#E57373' strokeWidth='2' />
    <rect x='22' y='4' width='14' height='14' rx='2' stroke='#E57373' strokeWidth='2' />
    <rect x='7' y='7' width='8' height='8' rx='1' fill='#E57373' />
    <rect x='7' y='25' width='8' height='8' rx='1' fill='#E57373' />
    <rect x='25' y='7' width='8' height='8' rx='1' fill='#E57373' />
    <path d='M22 22H28' stroke='#E57373' strokeWidth='2' strokeLinecap='round' />
    <path d='M22 28H28' stroke='#E57373' strokeWidth='2' strokeLinecap='round' />
    <path d='M34 22V28' stroke='#E57373' strokeWidth='2' strokeLinecap='round' />
    <path d='M34 34H28' stroke='#E57373' strokeWidth='2' strokeLinecap='round' />
    <path d='M22 34H22' stroke='#E57373' strokeWidth='2' strokeLinecap='round' />
  </svg>
);

const PRINT_HIDDEN_CLASS = 'invitation-print-hidden';
const PRINT_STYLE_ID = 'invitation-print-style';
const isAndroid = /Android/i.test(navigator.userAgent);

export const InvitationTicketGenerator = ({ data }: InvitationTicketGeneratorProps) => {
  const intl = useIntl();
  const ticketRef = useRef<HTMLDivElement>(null);
  const [isSaving, setIsSaving] = useState(false);

  const venue = data.event.arAddress || data.concert?.arAddress || data.match?.arAddress || '';
  const date = data.concert?.date || data.match?.date || '';
  const time = data.concert?.time || data.match?.time || '';

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ url });
        return;
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: intl.formatMessage({ id: 'invitation.ticket.linkCopied' }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch {
      // clipboard also unavailable — nothing to do
    }
  };

  const handlePrint = async () => {
    if (isAndroid) {
      if (!ticketRef.current) return;
      setIsSaving(true);
      try {
        const canvas = await html2canvas(ticketRef.current, {
          useCORS: true,
          scale: 2,
          backgroundColor: '#ffffff',
        });
        const link = document.createElement('a');
        link.download = 'ticket.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
      } finally {
        setIsSaving(false);
      }
      return;
    }

    const style = document.createElement('style');
    style.id = PRINT_STYLE_ID;
    style.textContent = `@media print { .${PRINT_HIDDEN_CLASS} { display: none !important; } }`;
    document.head.appendChild(style);
    window.onafterprint = () => {
      document.getElementById(PRINT_STYLE_ID)?.remove();
      window.onafterprint = null;
    };
    window.print();
  };

  return (
    <Flex flexDirection='column' minH='100vh' w='100vw' bg='white'>
      <Box className={PRINT_HIDDEN_CLASS}>
        <Navbar />
      </Box>
      <Flex flexDirection='column' align='center' flex='1' p={{ base: '16px', md: '24px' }}>
        <VStack spacing='20px' w='100%' maxW='480px'>
          <Box ref={ticketRef}>
            <Box boxShadow='0 2.5px 10px rgba(0, 0, 0, 0.15)' borderRadius='24px' w='100%'>
              {/* Event banner image */}
              {data.concert?.imageLink && (
                <Box borderTopRadius='24px' overflow='hidden'>
                  <Image
                    src={data.concert.imageLink}
                    alt={data.concert.title}
                    w='100%'
                    objectFit='cover'
                    sx={{ aspectRatio: '353 / 261' }}
                  />
                </Box>
              )}

              {/* Tear separator */}
              <Box position='relative' h='20px' bg='#F5F4F2' display='flex' alignItems='center'>
                <Box
                  position='absolute'
                  left='-12px'
                  w='24px'
                  h='24px'
                  borderRadius='50%'
                  bg='white'
                  zIndex={2}
                  boxShadow='inset -2px 0 4px rgba(0,0,0,0.06)'
                />
                <Box
                  flex={1}
                  mx='20px'
                  h='1px'
                  sx={{
                    backgroundImage:
                      'repeating-linear-gradient(90deg, #C4C4C4 0, #C4C4C4 6px, transparent 6px, transparent 14px)',
                  }}
                />
                <Box
                  position='absolute'
                  right='-12px'
                  w='24px'
                  h='24px'
                  borderRadius='50%'
                  bg='white'
                  zIndex={2}
                  boxShadow='inset 2px 0 4px rgba(0,0,0,0.06)'
                />
              </Box>

              {/* Category badge + holder name + Event info */}
              <Box bg='#F5F4F2' w='100%' px='20px' pt='20px' pb='16px'>
                <HStack justify='space-between' align='center' dir='rtl' mb='20px'>
                  <VStack align='flex-start' spacing='2px'>
                    <Text fontSize='xs' color='#888' fontWeight='medium'>
                      السيد/السيدة
                    </Text>
                    <Text fontSize='lg' fontWeight='bold' color='#1A1A1A'>
                      {data.holderName}
                    </Text>
                  </VStack>
                  <Badge
                    bg={data.category.color || '#D1FAE5'}
                    color='white'
                    fontSize='13px'
                    fontWeight='bold'
                    px='14px'
                    py='6px'
                    borderRadius='10px'
                    display='flex'
                    alignItems='center'
                  >
                    <Box as='span' borderRadius='50%' display='inline-block' />
                    {data.category.label}
                  </Badge>
                </HStack>

                {/* Event info: venue / time / date */}
                <HStack justify='space-between' spacing='8px' dir='rtl'>
                  <VStack spacing='4px' align='flex-start' flex='1'>
                    <Icon as={MapPinIcon} color='#E57373' boxSize='18px' />
                    <Text fontSize='xs' color='#888'>
                      المكان
                    </Text>
                    <Text
                      fontSize='sm'
                      fontWeight='bold'
                      color='#1A1A1A'
                      textAlign='center'
                      wordBreak='break-word'
                      whiteSpace='normal'
                    >
                      {venue}
                    </Text>
                  </VStack>

                  <VStack spacing='4px' align='flex-start' flex='1'>
                    <Icon as={ClockIcon} color='#E57373' boxSize='18px' />
                    <Text fontSize='xs' color='#888'>
                      الوقت
                    </Text>
                    <Text fontSize='sm' fontWeight='bold' color='#1A1A1A'>
                      {time}
                    </Text>
                  </VStack>

                  <VStack spacing='4px' align='flex-start' flex='1'>
                    <Icon as={CalendarIcon} color='#E57373' boxSize='18px' />
                    <Text fontSize='xs' color='#888'>
                      التاريخ
                    </Text>
                    <Text fontSize='sm' fontWeight='bold' color='#1A1A1A' textAlign='center'>
                      {date}
                    </Text>
                  </VStack>
                </HStack>
              </Box>

              {/* QR code section */}
              <Box
                bg='#FAFAF9'
                w='100%'
                px='20px'
                py='16px'
                borderBottomRadius='24px'
                boxShadow='0 4px 12px rgba(0, 0, 0, 0.1)'
              >
                {data.used ? (
                  <Box display='flex' justifyContent='center' py='8px'>
                    <QrCodeSuccess />
                  </Box>
                ) : data.qrCode ? (
                  <VStack spacing='16px' align='center'>
                    <VStack align='flex-end' spacing='4px' textAlign='right'>
                      <Text fontSize='md' fontWeight='bold' color='#1A1A1A'>
                        رمز الدخول
                      </Text>
                      <Text fontSize='xs' color='#666' lineHeight='1.6'>
                        اعرض هذا الرمز عند الباب صالح للاستخدام مرة واحدة
                      </Text>
                    </VStack>
                    <Box
                      p='10px'
                      bg='white'
                      borderRadius='12px'
                      border='1px solid'
                      borderColor='#E0E0E0'
                      boxShadow='0 2px 8px rgba(0,0,0,0.08)'
                      minW={'250px'}
                    >
                      <QrCode idToEncode={data.qrCode} />
                    </Box>
                  </VStack>
                ) : (
                  <HStack spacing='16px' align='center' textAlign='right'>
                    <Box
                      w='88px'
                      h='88px'
                      borderRadius='12px'
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                      flexShrink={0}
                      border='2.25px solid'
                      borderColor='#FFD4CC'
                      bg='linear-gradient(135deg, #FFE0DD 14.64%, #FFF1EF 85.36%)'
                    >
                      <QrPlaceholderIcon />
                    </Box>
                    <VStack align='flex-end' spacing='4px'>
                      <Text fontSize='md' fontWeight='bold' color='#1A1A1A'>
                        تذكرة الحدث
                      </Text>
                      <Text fontSize='xs' color='#666' lineHeight='1.6'>
                        سيظهر رمز التحقق قبل 6 ساعات من بدأ الحدث التقط صورة للشاشة عند ظهور الرمز
                        للذهاب إلى مكان الحدث
                      </Text>
                    </VStack>
                  </HStack>
                )}
              </Box>
            </Box>

            {/* Holder contact info */}
            <HStack
              dir='rtl'
              spacing='8px'
              align='center'
              w='100%'
              h='48px'
              px='12px'
              py='8px'
              borderRadius='14px'
              border='1px solid'
              borderColor='#E5E7EB'
              mt='20px'
            >
              <Box
                w='32px'
                h='32px'
                borderRadius='8px'
                overflow='hidden'
                flexShrink={0}
                display='flex'
                alignItems='center'
                justifyContent='center'
                bg='#F5F4F2'
              >
                <UserIcon size='1.5em' color='#FF5C4F' strokeWidth={1} />
              </Box>
              <VStack align='flex-start' spacing='1px'>
                <Text fontSize='sm' fontWeight='semibold' color='#1A1A1A' lineHeight='1.3'>
                  {data.holderName}
                </Text>
                <Text fontSize='xs' color='#888' dir='ltr' lineHeight='1.3'>
                  {data.holderPhone}
                </Text>
              </VStack>
            </HStack>
          </Box>

          {/* Action buttons */}
          <HStack
            w='100%'
            borderBottomRadius='20px'
            spacing='12px'
            dir='ltr'
            className={PRINT_HIDDEN_CLASS}
          >
            <Button
              flex='1'
              h='44px'
              borderRadius='9999px'
              border='1px solid'
              borderColor='#FF4D3D'
              bg='#FF4D3D'
              color='white'
              fontWeight='bold'
              fontSize='md'
              px='20px'
              gap='8px'
              _hover={{ bg: '#E53E2E', borderColor: '#E53E2E' }}
              _active={{ bg: '#CC3322', borderColor: '#CC3322' }}
              onClick={handleShare}
            >
              مشاركة
            </Button>
            <Button
              flex='1'
              h='44px'
              borderRadius='9999px'
              border='1px solid'
              borderColor='#E0E0E0'
              bg='white'
              color='#1A1A1A'
              fontWeight='bold'
              fontSize='md'
              px='20px'
              gap='8px'
              isLoading={isSaving}
              _hover={{ bg: '#F5F5F5' }}
              _active={{ bg: '#EBEBEB' }}
              onClick={handlePrint}
            >
              حفظ
            </Button>
          </HStack>
        </VStack>
      </Flex>
    </Flex>
  );
};
